import axios from "axios";

// Falls back to the page's own origin (not "") so URLs built for export (VAST tags, survey links)
// are absolute even when frontend and backend share an origin in production.
export const BACKEND = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
export const API = `${BACKEND}/api`;

const TOKEN_KEY = "cts-token";
const USER_KEY = "cts-user";
export function getToken() { return localStorage.getItem(TOKEN_KEY); }
export function setToken(t) { localStorage.setItem(TOKEN_KEY, t); }
export function clearToken() { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(USER_KEY); }
export function isAuthed() { return !!getToken(); }
export function getUser() { try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); } catch { return null; } }
export function setUser(u) { localStorage.setItem(USER_KEY, JSON.stringify(u)); }
function headers() { const t = getToken(); return t ? { Authorization: `Bearer ${t}` } : {}; }

function persist(data) { setToken(data.token); if (data.user) setUser(data.user); return data; }

export async function login(email, password) {
  const { data } = await axios.post(`${API}/auth/login`, { email, password });
  return persist(data);
}
export async function register(email, password, name) {
  const { data } = await axios.post(`${API}/auth/register`, { email, password, name });
  return persist(data);
}
export async function googleLogin(credential) {
  const { data } = await axios.post(`${API}/auth/google`, { credential });
  return persist(data);
}
export async function getPublicConfig() {
  const { data } = await axios.get(`${API}/config`);
  return data;
}
export async function authMe() {
  const { data } = await axios.get(`${API}/auth/me`, { headers: headers() });
  if (data.user) setUser(data.user);
  return data.user;
}

// ---- Platform admin ----
export async function adminListCompanies(status) {
  const params = status ? { status } : {};
  const { data } = await axios.get(`${API}/admin/companies`, { params, headers: headers() });
  return data;
}
export async function approveCompany(id) {
  const { data } = await axios.post(`${API}/admin/companies/${id}/approve`, {}, { headers: headers() });
  return data;
}
export async function rejectCompany(id) {
  const { data } = await axios.post(`${API}/admin/companies/${id}/reject`, {}, { headers: headers() });
  return data;
}
export async function reopenCompany(id) {
  const { data } = await axios.post(`${API}/admin/companies/${id}/reopen`, {}, { headers: headers() });
  return data;
}
export async function pauseCompany(id) {
  const { data } = await axios.post(`${API}/admin/companies/${id}/pause`, {}, { headers: headers() });
  return data;
}
export async function adminListUsers() {
  const { data } = await axios.get(`${API}/admin/users`, { headers: headers() });
  return data;
}
export async function adminListTemplates() {
  const { data } = await axios.get(`${API}/admin/templates`, { headers: headers() });
  return data;
}
export async function promoteTemplate(id) {
  const { data } = await axios.post(`${API}/admin/templates/${id}/promote`, {}, { headers: headers() });
  return data;
}
export async function adminDeleteTemplate(id) {
  const { data } = await axios.delete(`${API}/admin/templates/${id}`, { headers: headers() });
  return data;
}
export async function adminGetWhatsapp() {
  const { data } = await axios.get(`${API}/admin/whatsapp`, { headers: headers() });
  return data;
}
export async function adminSaveWhatsapp(payload) {
  const { data } = await axios.put(`${API}/admin/whatsapp`, payload, { headers: headers() });
  return data;
}
export async function deleteOwnTemplate(id) {
  const { data } = await axios.delete(`${API}/templates/${id}`, { headers: headers() });
  return data;
}
export async function deleteCompany(id) {
  const { data } = await axios.delete(`${API}/admin/companies/${id}`, { headers: headers() });
  return data;
}

// ---- Company / team ----
export async function listCompanyUsers() {
  const { data } = await axios.get(`${API}/company/users`, { headers: headers() });
  return data;
}
export async function inviteTeammate(email) {
  const { data } = await axios.post(`${API}/company/invite`, { email }, { headers: headers() });
  return data;
}
export async function cancelInvite(email) {
  const { data } = await axios.delete(`${API}/company/invites/${encodeURIComponent(email)}`, { headers: headers() });
  return data;
}
export async function setUserRole(uid, role) {
  const { data } = await axios.patch(`${API}/company/users/${uid}/role`, { role }, { headers: headers() });
  return data;
}
export async function removeUser(uid) {
  const { data } = await axios.delete(`${API}/company/users/${uid}`, { headers: headers() });
  return data;
}

// ---- Creatives ----
export async function listCreatives(search) {
  const params = search ? { search } : {};
  const { data } = await axios.get(`${API}/creatives`, { params, headers: headers() });
  return data;
}
export async function getCreative(id) {
  const { data } = await axios.get(`${API}/creatives/${id}`, { headers: headers() });
  return data;
}
export async function createCreative(body) {
  const { data } = await axios.post(`${API}/creatives`, body, { headers: headers() });
  return data;
}
export async function updateCreative(id, config, name) {
  const { data } = await axios.put(`${API}/creatives/${id}`, { config, name }, { headers: headers() });
  return data;
}
export async function getCreativeOutput(id) {
  const { data } = await axios.get(`${API}/creatives/${id}/output`, { headers: headers() });
  return data;
}
export async function renameCreative(id, name) {
  const { data } = await axios.patch(`${API}/creatives/${id}/rename`, { name }, { headers: headers() });
  return data;
}
export async function duplicateCreative(id) {
  const { data } = await axios.post(`${API}/creatives/${id}/duplicate`, {}, { headers: headers() });
  return data;
}
export async function deleteCreative(id) {
  await axios.delete(`${API}/creatives/${id}`, { headers: headers() });
}

// ---- Templates (SDK registry) ----
export async function listTemplates() {
  const { data } = await axios.get(`${API}/templates`, { headers: headers() });
  return data;
}
export async function getTemplateManifest(id) {
  const { data } = await axios.get(`${API}/templates/${id}`, { headers: headers() });
  return data;
}
export async function uploadTemplate(manifestFile, bundleFile, thumbFile) {
  const fd = new FormData();
  fd.append("manifest", manifestFile);
  fd.append("bundle", bundleFile);
  if (thumbFile) fd.append("thumbnail", thumbFile);
  const { data } = await axios.post(`${API}/templates`, fd, { headers: headers() });
  return data;
}
export function sdkFrameUrl(templateId) { return `${API}/sdk/frame?t=${encodeURIComponent(templateId)}`; }
export async function uploadAsset(file, creativeId, templateId) {
  const fd = new FormData();
  fd.append("file", file);
  if (creativeId) fd.append("creativeId", creativeId);
  if (templateId) fd.append("templateId", templateId);
  const { data } = await axios.post(`${API}/assets`, fd, { headers: headers() });
  return data;
}
export async function getEvents(creativeId, range) {
  const params = {};
  if (creativeId) params.creativeId = creativeId;
  if (range?.from) params.from = range.from;
  if (range?.to) params.to = range.to;
  const { data } = await axios.get(`${API}/events`, { params, headers: headers() });
  return data;
}
export async function downloadEventsCsv(creativeId, range) {
  const params = {};
  if (creativeId) params.creativeId = creativeId;
  if (range?.from) params.from = range.from;
  if (range?.to) params.to = range.to;
  const res = await axios.get(`${API}/events/export`, { params, headers: headers(), responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = `events_${creativeId || "all"}.csv`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export async function fetchVast(ref) {
  const { data } = await axios.get(`${API}/vast/${ref}`, { responseType: "text" });
  return data;
}
export function tagUrl(ref) { return `${API}/vast/${ref}`; }
export function previewUrl(id) { return `${API}/c/${id}?preview=1`; }
export function hostUrl(id) { return `${API}/simid-host/${id}`; }

// ---- In-app documentation ----
export async function listDocs() {
  const { data } = await axios.get(`${API}/docs`, { headers: headers() });
  return data;
}
export async function getDoc(slug) {
  const { data } = await axios.get(`${API}/docs/${slug}`, { headers: headers() });
  return data;
}


export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
